import { useEffect, useRef } from "react";

/**
 * Live audio waveform driven by a Web Audio AnalyserNode (mic + assistant audio).
 * Renders animated bars on a canvas. Shows a calm idle line when not active.
 */
export function Waveform({
  getAnalyser,
  active,
}: {
  getAnalyser: () => AnalyserNode | null;
  active: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const BARS = 48;
    let phase = 0;

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const analyser = active ? getAnalyser() : null;
      const mid = h / 2;
      const gap = 3;
      const barW = Math.max(2, (w - (BARS - 1) * gap) / BARS);

      let data: Uint8Array<ArrayBuffer> | null = null;
      if (analyser) {
        const buf = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(buf);
        data = buf;
      }

      const grad = ctx.createLinearGradient(0, 0, w, 0);
      grad.addColorStop(0, "#4d7cff");
      grad.addColorStop(1, "#7c5bff");
      ctx.fillStyle = grad;

      phase += 0.08;
      for (let i = 0; i < BARS; i++) {
        let amp: number;
        if (data) {
          const idx = Math.floor((i / BARS) * (data.length * 0.6));
          amp = (data[idx] / 255) * (h * 0.9);
        } else {
          // Idle: gentle sine shimmer.
          amp = (Math.sin(phase + i * 0.4) * 0.5 + 0.5) * (active ? 6 : 3) + 2;
        }
        const x = i * (barW + gap);
        const bh = Math.max(2, amp);
        const r = barW / 2;
        roundRect(ctx, x, mid - bh / 2, barW, bh, r);
        ctx.fill();
      }
    };
    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, [getAnalyser, active]);

  return <canvas ref={canvasRef} className="waveform" />;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
